import { Component, OnInit, Input, ViewChild, ElementRef, NgZone, OnDestroy, AfterViewInit, OnChanges } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';

const COLORS = ['#216FB8', '#33ABE2', '#33ABE2'];

@Component({
  selector: 'app-ac-recommendations-chart',
  templateUrl: './ac-recommendations-chart.component.html',
  styleUrls: ['./ac-recommendations-chart.component.scss']
})
export class AcRecommendationsChartComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {

  private chart?: am4charts.XYChart;
  private language: string;

  @Input() data: any;
  @ViewChild('chartContainer') chartContainer?: ElementRef = null;

  constructor(
    private zone: NgZone,
    private transloco: TranslocoService
  ) { }

  ngOnInit(): void {
    this.language = this.transloco.getActiveLang().toUpperCase();
  }

  ngOnChanges(): void {
    this.setChartData();
  }

  ngOnDestroy(): void {
    this.zone.runOutsideAngular(() => {
      this.chart?.dispose();
    });
  }

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      if (this.chartContainer === null) {
        return;
      }
      am4core.useTheme(am4themes_animated);

      const chart = am4core.create(this.chartContainer.nativeElement, am4charts.XYChart);
      chart.height = 300;

      const title = chart.titles.create();
      title.text = this.transloco.translate('statistics.acs.recommendations');
      title.marginBottom = 10;

      const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
      categoryAxis.dataFields.category = 'name';
      categoryAxis.renderer.minGridDistance = 20;

      const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
      valueAxis.min = 0;
      valueAxis.renderer.minGridDistance = 30;

      const label = categoryAxis.renderer.labels.template;
      label.truncate = true;
      label.maxWidth = 120;

      chart.colors.list = COLORS.map((entry: string) => {
        return am4core.color(entry);
      });

      const series = chart.series.push(new am4charts.ColumnSeries());
      series.dataFields.valueY = 'value';
      series.dataFields.categoryX = 'name';

      series.columns.template.adapter.add('fill', (fill, target) => {
        return chart.colors.getIndex(target.dataItem.index);
      });

      series.columns.template.adapter.add('stroke', (stroke, target) => {
        return chart.colors.getIndex(target.dataItem.index);
      });

      // tooltip
      const tooltipText = '{name}\n';
      series.columns.template.tooltipText = tooltipText;

      const valueLabel = series.bullets.push(new am4charts.LabelBullet());
      valueLabel.label.text = '{value}';
      valueLabel.label.fontSize = 14;
      valueLabel.label.adapter.add('dy', (dy, target) => {
        if (!target.dataItem) {
          return dy;
        }
        return (target.dataItem.values.valueY.value < 1) ? -10 : 10;
      });
      valueLabel.label.adapter.add('fill', (fill, target) => {
        if (!target.dataItem) {
          return fill;
        }
        return (target.dataItem.values.valueY.value === 0) ? am4core.color('#000') : am4core.color('#fff');
      });

      this.chart = chart;
      this.setChartData();
    });
  }

  private setChartData(): any {
    if (this.chart && this.data) {
      const data: Array<any> = [];
      Object.keys(this.data).forEach((key: string) => {
        data.push({
          name: this.data[key]?.name[this.language],
          value: this.data[key]?.value
        });
      });
      this.chart.data = data;
    }
  }

}
